import { fetchPvRecCrossCategoryRos } from '@api/pvRecCrossCategoryRos';
import { useEffect, useRef, useState } from 'react';

const DEFAULT_VENDORS_PER_CATEGORY = 8;

interface UseVendorsResult {
	vendorsByCategory: Record<string, Vendor.Raw[]>;
	selectedCategoryVendors: Vendor.Raw[];
	isLoading: boolean;
	error: string | null;
}

export const useVendors = (
	vendorId: string | null,
	selectedCategoryCode: string | null,
	currentMemberId: string,
	conversations: Record<string, unknown>,
): UseVendorsResult => {
	const [vendorsByCategory, setVendorsByCategory] = useState<
		Record<string, Vendor.Raw[]>
	>({});
	const [isLoading, setIsLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const inFlightRequestsRef = useRef<Map<string, Promise<Vendor.Raw[]>>>(
		new Map(),
	);

	useEffect(() => {
		if (!selectedCategoryCode) return;

		if (!vendorId) return;

		const cachedVendors = vendorsByCategory[selectedCategoryCode];
		if (cachedVendors !== undefined) {
			setError(null);
			setIsLoading(false);
			return;
		}

		const requestKey = `${vendorId}:${selectedCategoryCode}:${DEFAULT_VENDORS_PER_CATEGORY}`;

		let cancelled = false;
		setIsLoading(true);
		setError(null);

		const loadVendors = async () => {
			let request = inFlightRequestsRef.current.get(requestKey);
			if (request === undefined) {
				request = fetchPvRecCrossCategoryRos(
					vendorId,
					DEFAULT_VENDORS_PER_CATEGORY,
					selectedCategoryCode,
				).then((response) => {
					const matchingRecommendation = response.recommendations.find(
						(recommendation) =>
							recommendation.categoryCode === selectedCategoryCode,
					);

					return matchingRecommendation?.profiles ?? [];
				});

				inFlightRequestsRef.current.set(requestKey, request);
			}

			try {
				const vendors = await request;

				if (cancelled) {
					return;
				}

				setVendorsByCategory((prev) => ({
					...prev,
					[selectedCategoryCode]: vendors,
				}));
				setError(null);
			} catch (_err) {
				if (cancelled) {
					return;
				}

				setError('Failed to load vendors');
			} finally {
				inFlightRequestsRef.current.delete(requestKey);

				if (!cancelled) {
					setIsLoading(false);
				}
			}
		};

		void loadVendors();

		return () => {
			cancelled = true;
		};
	}, [vendorId, selectedCategoryCode, vendorsByCategory]);

	const allCategoryVendors =
		selectedCategoryCode !== null
			? (vendorsByCategory[selectedCategoryCode] ?? [])
			: [];

	const selectedCategoryVendors = (() => {
		if (currentMemberId === '') return allCategoryVendors.slice(0, 4);

		return allCategoryVendors
			.filter((vendor) => !conversations[vendor.id])
			.slice(0, 4);
	})();

	return {
		vendorsByCategory,
		selectedCategoryVendors,
		isLoading,
		error,
	};
};
